import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { ButtonComponent } from 'components/ButtonComponent';
import InputText from 'components/FormCreator/inputs/InputText';
import { fieldType } from 'components/FormCreator/types';
import FormCreatorComponent from 'components/FormCreatorComponent';
import CheckBox from 'components/FormCreatorComponent/inputs/CheckBox';
import TextArea from 'components/FormCreatorComponent/inputs/TextArea';
import { createFormProps } from 'components/FormCreatorComponent/types';
import { PopupComponent } from 'components/PopupComponent';
import { BalanceStatus } from 'constants/balance.constants';
import { withdrawalBalanceSteps } from 'constants/customer-account.constants';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import _ from 'lodash';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { withdrawalBalanceTypesForms } from 'sections/CustomerAccounts/forms.types';
import { getWithdrawalFormsValuesByTypeAndStep } from 'sections/CustomerAccounts/helper';
import { getCompanyFeatureApprovePayload } from 'sections/pendingRequests/helper';
import {
	approveFeature,
	createSupportWithdrawalRequest,
	withdrawalWaitOnHouseAccountApproval,
} from 'services/pending-requests.service';
import { getCompanyDashboard } from 'state/reducers/customer-accounts.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';

interface IWithdrawBalanceStep1Props {
	supportRequest: any;
	withdrawalBalanceForm: createFormProps;
	dispatchGetSupportRequest: any;
	companyId: number;
}

export const WithdrawBalanceStep1 = (props: IWithdrawBalanceStep1Props) => {
	const { supportRequest, withdrawalBalanceForm, dispatchGetSupportRequest, companyId } = props;
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const theme = useTheme();
	const [approveWithdraw, setApproveWithdraw] = useState<string>('false');
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [formState, setFormState] = useState<Record<string, any>>({});
	const [openCancelPopup, setOpenCancelOpenPopup] = useState(false);
	const [amountExceededPopup, setAmountExceededPopup] = useState(false);
	const [cancelReason, setCancelReason] = useState<string | null>(null);
	const [balanceActivityId, setBalanceActivityId] = useState<number | null>(null);
	const companyData: any = useAppSelector(getCompanyDashboard);
	const isContinueDisabled = useMemo(
		() => !formState.isValid || approveWithdraw !== 'true',
		[formState, approveWithdraw]
	);
	const [supportRequestInUse, setSupportRequestInUse] = useState<Record<string, any> | null>(null);
	const isRejectDisabled = useMemo(() => !cancelReason || !cancelReason.length, [cancelReason]);
	const [transferSuccessPopup, setTransferSuccessPopup] = useState(false);
	const [transferErrorPopup, setTransferErrorPopup] = useState(false);
	const currentBalance = useMemo(() => {
		return companyData.balances.find((balance: any) => balance.currencyId === formValues.currency_id);
	}, [formValues]);

	const isHandleRejectDisabled = useMemo(() => {
		return !_.keys(supportRequest).length;
	}, [supportRequest]);

	useEffect(() => {
		// if (!_.isEmpty(supportRequest)) {
		const pickFormValues = getWithdrawalFormsValuesByTypeAndStep(
			supportRequest,
			withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST
		);
		setFormValues(pickFormValues);
		//
	}, [supportRequest]);

	useEffect(() => {
		if (formState?.values) setFormValues(formState?.values);
	}, [formState]);

	const handleContinue = async () => {
		if (currentBalance.amount < Number(formValues.amount)) {
			setAmountExceededPopup(true);
			return;
		}

		if (!_.keys(supportRequest).length) {
			dispatch(
				createSupportWithdrawalRequest({
					contactId: formValues.contact_id,
					companyId,
					closeAccount: formValues?.close_account === 'true',
				})
			)
				.unwrap()
				.then((originalPromiseResult) => {
					if (!originalPromiseResult) {
						setTransferErrorPopup(true);
					} else {
						setSupportRequestInUse(originalPromiseResult.supportRequest);
					}
				})
				.catch((rejectedValueOrSerializedError) => {});
		} else {
			setSupportRequestInUse(supportRequest);
		}
	};

	const handleCancel = async () => {
		setOpenCancelOpenPopup(!openCancelPopup);
		setCancelReason(null);
	};

	const handleReject = async () => {
		const props = getCompanyFeatureApprovePayload(
			'reject',
			pendingRequestsTypes.WITHDRAWAL_SUPPORT,
			supportRequest.id,
			cancelReason!
		);
		if (props.name !== '') {
			dispatch(approveFeature(props))
				.unwrap()
				.then(async (originalPromiseResult) => {
					navigate(-1);
				})
				.catch((rejectedValueOrSerializedError) => {
					// handle error here
					console.log(rejectedValueOrSerializedError);
				});
		}
	};

	useEffect(() => {
		if (supportRequestInUse) {
			const props = getCompanyFeatureApprovePayload(
				'approve',
				pendingRequestsTypes.WITHDRAWAL_SUPPORT,
				supportRequestInUse.id,
				formValues.reason,
				{
					approveWithdraw: approveWithdraw === 'true',
					amount: Number(formValues.amount),
					currencyId: formValues.currency_id,
					contactId: formValues.contact_id,
				}
			);
			if (props.name !== '') {
				dispatch(approveFeature(props))
					.unwrap()
					.then((originalPromiseResult) => {
						if (!originalPromiseResult) {
							setTransferErrorPopup(true);
						} else {
							const { balanceRequest, supportRequest } = originalPromiseResult;
							//setTransferSuccessPopup(true);
							setBalanceActivityId(balanceRequest.id);
							handleContinueToStep2();
						}
					})
					.catch((rejectedValueOrSerializedError) => {});
			}
		}
	}, [supportRequestInUse]);

	const handleContinueToStep2 = () => {
		setTransferSuccessPopup(false);
		const url = window.location.toString();
		window.location.href = url.replace('new', supportRequestInUse!.id);
	};
	const handleContinueToStep3 = () => {
		const url = window.location.toString();
		setTransferSuccessPopup(false);

		if (balanceActivityId) {
			dispatch(
				withdrawalWaitOnHouseAccountApproval({
					balanceActivityId,
					isNotRequired: true,
				})
			)
				.unwrap()
				.then((originalPromiseResult) => {
					window.location.href = url.replace('new', supportRequestInUse!.id);
				})
				.catch((rejectedValueOrSerializedError) => {});
		}
	};

	return (
		<Box marginTop="40px" maxWidth="664px" paddingLeft="96px">
			{!_.isEmpty(formValues) && withdrawalBalanceForm && (
				<>
					<PopupComponent
						open={transferSuccessPopup}
						title={t('page.withdrawal.popups.success_cc_transfer_sub_to_house.title')}
						buttonText={t('button.cancel_request')}
						handleClick={() => {}}
						isDefaultButton={false}
						buttonVariant="outlined"
					>
						<Stack direction={'column'} width={'100%'} sx={{ direction: 'ltr' }}>
							<Typography variant="h4">
								{t('page.withdrawal.popups.success_cc_transfer_sub_to_house.subtitle')}
							</Typography>
							<Typography marginTop="12px" variant="h6">
								{t('page.withdrawal.popups.success_cc_transfer_sub_to_house.description')}
							</Typography>
							<Stack marginTop="24px">
								<ButtonComponent
									disabled={!balanceActivityId}
									handleClick={() => handleContinueToStep2()}
									text={t(
										'page.withdrawal.popups.success_cc_transfer_sub_to_house.need_house_account_approval_button'
									)}
									styleObj={{
										borderRadius: '8px',
										width: '100%',
										height: '48px',
									}}
								/>
								<ButtonComponent
									disabled={false}
									handleClick={() => handleContinueToStep3()}
									text={t(
										'page.withdrawal.popups.success_cc_transfer_sub_to_house.no_need_house_account_approval_button'
									)}
									styleObj={{
										borderRadius: '8px',
										width: '100%',
										height: '48px',
										marginTop: '12px',
									}}
									variantType="outlined"
								/>
							</Stack>
						</Stack>
					</PopupComponent>
					<PopupComponent
						open={transferErrorPopup}
						title={t('page.withdrawal.popups.failed_cc_transfer_sub_to_house.title')}
						buttonText={t('button.cancel_request')}
						handleClick={() => {}}
						isDefaultButton={false}
						buttonVariant="outlined"
					>
						<Stack direction={'column'} width={'100%'}>
							<Typography variant="h4">
								{t('page.withdrawal.popups.failed_cc_transfer_sub_to_house.subtitle')}
							</Typography>
							<Typography marginTop="12px" variant="h6">
								{t('page.withdrawal.popups.success_cc_transfer_sub_to_house.description')}
							</Typography>
							<Stack marginTop="24px">
								<ButtonComponent
									disabled={false}
									handleClick={() => setTransferErrorPopup(false)}
									text={t('page.withdrawal.popups.failed_cc_transfer_sub_to_house.try_again_button')}
									styleObj={{
										borderRadius: '8px',
										width: '100%',
										height: '48px',
									}}
								/>
								<ButtonComponent
									disabled={false}
									handleClick={() => handleReject()}
									text={t(
										'page.withdrawal.popups.failed_cc_transfer_sub_to_house.cancel_request_button'
									)}
									styleObj={{
										borderRadius: '8px',
										width: '100%',
										height: '48px',
										marginTop: '12px',
									}}
									variantType="outlined"
								/>
							</Stack>
						</Stack>
					</PopupComponent>
					<PopupComponent
						open={openCancelPopup}
						title={t('page.withdrawal.popups.reject_withdrawal_initial_request.title')}
						buttonText={t('button.cancel_request')}
						handleClick={() => {}}
						isDefaultButton={false}
						buttonVariant="outlined"
					>
						<Stack direction={'column'} width={'100%'}>
							<InputText
								initialValidation={{}}
								handleChange={() => {}}
								valid={true}
								cField={{
									type: fieldType.text,
									props: {
										name: 'status_input',
										label: t(
											'page.withdrawal.popups.reject_withdrawal_initial_request.status_input.label'
										),
										value: t(
											'page.withdrawal.popups.reject_withdrawal_initial_request.status_input.text'
										),
										disabled: true,
									},
									styleObj: {
										alignSelf: 'end',
									},
								}}
							/>
							<InputText
								initialValidation={{}}
								handleChange={(event: any) => {
									setCancelReason(event.target.value);
								}}
								valid={true}
								cField={{
									type: fieldType.textArea,
									props: {
										name: 'status_input',
										label: t(
											'page.withdrawal.popups.reject_withdrawal_initial_request.cancel_reason.title'
										),
										value: cancelReason,
										placeholder: t(
											'page.withdrawal.popups.reject_withdrawal_initial_request.cancel_reason.text'
										),
										disabled: false,
									},
									styleObj: {
										marginTop: '20px',
									},
								}}
							/>
							<Stack direction={'row'} alignSelf={'start'} marginTop="30px">
								<ButtonComponent
									disabled={isRejectDisabled}
									handleClick={() => handleReject()}
									text={t('button.confirm_and_close')}
									styleObj={{
										borderRadius: '8px',
										width: 'max-content',
										height: '48px',
									}}
								/>
								<ButtonComponent
									disabled={false}
									handleClick={handleCancel}
									text={t('button.back')}
									styleObj={{
										borderRadius: '8px',
										width: 'max-content',
										height: '48px',
										marginRight: '12px',
									}}
									variantType="outlined"
								/>
							</Stack>
						</Stack>
					</PopupComponent>
					<PopupComponent
						open={amountExceededPopup}
						title={t('page.withdrawal.popups.amount_exceeded.title')}
						buttonText={''}
						handleClick={() => {}}
						buttonVariant="outlined"
						isDefaultButton={false}
					>
						<Stack direction={'column'} alignItems={'center'} width={'100%'} marginTop="-24px">
							<Typography sx={{ color: theme.palette.root.bolderGray }} variant="body">
								{t('page.withdrawal.popups.amount_exceeded.subtitle')}
							</Typography>
							<Typography variant="body" sx={{ width: '100%', color: theme.palette.root.bolderGray }}>
								{t('page.withdrawal.popups.amount_exceeded.body')}
							</Typography>
							<ButtonComponent
								disabled={false}
								handleClick={() => {
									setAmountExceededPopup(false);
								}}
								text={t('button.try_again')}
								styleObj={{
									borderRadius: '8px',
									width: '100%',
									height: '48px',
									marginTop: '24px',
								}}
							/>
							<ButtonComponent
								disabled={false}
								handleClick={() => {
									setAmountExceededPopup(false);
								}}
								text={t('button.cancel_request')}
								styleObj={{
									borderRadius: '8px',
									marginTop: '24px',
									width: '100%',
									height: '48px',
								}}
								variantType="outlined"
							/>
						</Stack>
					</PopupComponent>
					<FormCreatorComponent
						{...withdrawalBalanceForm}
						translation={t}
						values={formValues}
						handleChange={(formState: Record<string, any>) => {
							setFormState(formState);
						}}
					/>
					<Divider sx={{ marginTop: '68px', marginBottom: '20px' }} />
					<CheckBox
						valid={true}
						handleChange={(event: any) => setApproveWithdraw(event.target.value)}
						initialValidation={[]}
						cField={{
							type: fieldType.selectBox,
							props: {
								name: '',
								value: approveWithdraw,
								label: t(
									'page.withdrawal.forms.withdrawal_initial_request.checkbox_withdrawal_approval.title'
								),
								helperText: t(
									'page.withdrawal.forms.withdrawal_initial_request.checkbox_withdrawal_approval.description'
								),
							},
						}}
					/>
					<Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', marginTop: '48px' }}>
						<ButtonComponent
							disabled={false}
							text={t('page.withdrawal.forms.withdrawal_initial_request.cancel_button')}
							handleClick={() => navigate(-1)}
						/>
						<ButtonComponent
							styleObj={{ marginLeft: '24px' }}
							disabled={isContinueDisabled}
							text={t('page.withdrawal.forms.withdrawal_initial_request.continue_button')}
							handleClick={() => handleContinue()}
						/>
					</Box>
				</>
			)}
		</Box>
	);
};
